import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import type { RoleResponse } from '../../../../../api/models/role-response';
import { RoleMappingUtil } from '../../../../utils/role-mapping.util';

/**
 * Read display for a user-roles field (`custom-userroles-multicheckbox`, #21239): the role NAMES.
 *
 * The field stores role ids; without this display the read row printed
 * "abf34731-4cd6-47d6-2d55-08ddff655f21". Names come from `formState.userRoles`, which
 * `DetailsService` publishes, and are mapped by the same `RoleMappingUtil` the list column uses
 * (`UtilityService.valueToInterfaceValue`), so a role reads identically in the list and here —
 * including its fallback: a role id the store does not know stays visible as the id rather than
 * silently disappearing.
 *
 * Presentational: both inputs come from the row, the role list from the field's formState.
 */
@Component({
  selector: 'dd-user-roles-display',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `{{ text() }}`,
})
export class UserRolesDisplayComponent extends ReadonlyDisplayBase {
  readonly text = computed(() => {
    const ids = Array.isArray(this.value()) ? (this.value() as unknown[]).filter((id): id is string => typeof id === 'string' && !!id) : [];
    if (ids.length === 0) {
      return '—';
    }
    const source = (this.field().options?.formState as { userRoles?: RoleResponse[] | (() => RoleResponse[]) } | undefined)?.userRoles;
    // Read the store signal here so a late role response updates the existing row.
    const roles = (typeof source === 'function' ? source() : source) ?? [];
    return RoleMappingUtil.mapRoleIdsToNames(ids, roles);
  });
}
